import { useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarApi } from '../api';
import { CalendarEvent } from '@demind-inc/core';
import { TEAM_DAY_QUERY_KEY } from '../queries/useTeamDay';

interface UseDeleteCalendarEventParams {
  calendarId: string;
  userId: string;
  eventId: string;
  unSyncToCalendar?: boolean;
  recurringDeleteSpan?: 'thisEvent' | 'allEvents';
}

export function useDeleteCalendarEvent() {
  const queryClient = useQueryClient();

  const deleteCalendarEventMutation = useMutation({
    mutationFn: ({
      calendarId,
      eventId,
      userId,
      unSyncToCalendar,
      recurringDeleteSpan,
    }: UseDeleteCalendarEventParams) => {
      return calendarApi.deleteCalendarEvent(
        userId,
        calendarId,
        eventId,
        unSyncToCalendar,
        recurringDeleteSpan
      );
    },
    onMutate: async ({ eventId: targetEventId }: UseDeleteCalendarEventParams) => {
      await queryClient.cancelQueries({ queryKey: ['lifestack.calendar.events'] });
      queryClient.setQueriesData<CalendarEvent[]>(
        { queryKey: ['lifestack.calendar.events'] },
        (prevEvents) => {
          return prevEvents?.filter(({ eventId }) => eventId !== targetEventId);
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
      queryClient.invalidateQueries({ queryKey: TEAM_DAY_QUERY_KEY, type: 'active' });

      queryClient.invalidateQueries({
        queryKey: ['lifestack.calendar.allDayEvents'],
      });
    },
  });

  return {
    deleteCalendarEvent: deleteCalendarEventMutation.mutateAsync,
    isDeleting: deleteCalendarEventMutation.isPending,
    ...deleteCalendarEventMutation,
  };
}
